/**
 * usePartnerConnection — surfaces the current user's connection record for a
 * given partner ID and exposes the favorite-toggle mutation.
 *
 * Used by every entry point that opens ProfilePreviewModal on a connected
 * partner (Network page, Messages thread, Referral detail). Centralizing the
 * lookup and mutation means each call site can pass `showFavoriteButton`,
 * `showMenuButton`, `currentUserOrganizationId`, and `onToggleFavorite` to the
 * modal without duplicating the connection-list query or the favorite PATCH.
 */

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';

interface ConnectionRecord {
  id: string;
  connectionId?: string;
  isFavorite?: boolean;
  status?: string;
  organizationId?: string | null;
  [key: string]: unknown;
}

export function usePartnerConnection(partnerId: string | null | undefined) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data: connections } = useQuery({
    queryKey: ['/api/workflows/connections'],
    queryFn: () => api.get('/api/workflows/connections'),
    enabled: !!partnerId,
    staleTime: 30_000,
  });

  const connection = useMemo<ConnectionRecord | undefined>(() => {
    if (!partnerId) return undefined;
    const list = Array.isArray(connections) ? (connections as ConnectionRecord[]) : [];
    return list.find((c) => c.id === partnerId);
  }, [connections, partnerId]);

  const toggleFavoriteMutation = useMutation({
    mutationFn: async () => {
      const connectionId = connection?.connectionId || connection?.id;
      if (!connectionId) throw new Error('No connection found for this partner');

      const currentStatus = connection?.isFavorite || false;
      const newStatus = !currentStatus;

      queryClient.setQueryData(['/api/workflows/connections'], (old: unknown) => {
        if (!Array.isArray(old)) return old;
        return old.map((c: ConnectionRecord) =>
          c.id === partnerId ? { ...c, isFavorite: newStatus } : c
        );
      });

      await api.patch(`/api/workflows/connections/${connectionId}/favorite`, {
        isFavorite: newStatus,
      });

      return { currentStatus };
    },
    onSuccess: ({ currentStatus }) => {
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/connections'] });
      showSuccess(
        currentStatus ? 'Removed from Favorites' : 'Added to Favorites',
        currentStatus
          ? 'Partner removed from your favorites'
          : 'Partner added to your favorites'
      );
    },
    onError: (error: Error) => {
      // Revert by refetching authoritative data
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/connections'] });
      showError('Update Failed', error.message || 'Failed to update favorite status');
    },
  });

  const isConnected =
    connection?.status === 'connected' || connection?.status === 'accepted';

  return {
    connection,
    connectionId: connection?.connectionId || connection?.id,
    isFavorite: connection?.isFavorite || false,
    isConnected,
    toggleFavorite: () => toggleFavoriteMutation.mutate(),
    isTogglingFavorite: toggleFavoriteMutation.isPending,
  };
}
